import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // ** Api base url
  apiRoute = process.env.REACT_APP_API_BASE_URL

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // ** Request Interceptor
    axios.interceptors.request.use(
      config => {
        // ** Get token from localStorage
        const accessToken = this.getToken()

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    // ** Add request/response interceptor
    axios.interceptors.response.use(
      response => response,
      error => {
        // ** const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        // ** if (status === 401) {
        if (response && response.status === 401) {
          return Promise.reject(error)
        }
        return Promise.reject(error)
      }
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  async login(...args) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.post(
      `${this.apiRoute}${this.jwtConfig.loginEndpoint}`,
      ...args,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async forgotPassword(...args) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.post(
      `${this.apiRoute}/user/customer/recoveryPassword`,
      ...args,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async changePassword(password) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.put(
      `${this.apiRoute}/user/customer`,
      {
        password
      },
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async getUserData() {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.get(
      `${this.apiRoute}/user/customer`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async getDocuments() {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.get(
      `${this.apiRoute}/user/documents`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async getDocument(documentId) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response = axios.get(
      `${this.apiRoute}/user/document/${documentId}`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async getPortions(documentId) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response =  axios.get(
      `${this.apiRoute}/user/portions/${documentId}`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async viewDocument(documentId) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response =  axios.get(
      `${this.apiRoute}/user/document/visualization/${documentId}`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async requestRenewal(userId, documentId) {
    const headers = {
      'Content-Type': 'application/json'
    }
    const response =  axios.get(
      `${this.apiRoute}/user/policyRenewal/${userId}/${documentId}`,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }

  async requestDuplicate(userId, documentId, portion) {
    const formData = new FormData()
    formData.append('portion', portion)
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response =  axios.post(
      `${this.apiRoute}/user/requestPayment/${userId}/${documentId}`,
      formData,
      {
        headers
      }
    )
    .then((response) => {
      return Promise.resolve(response)
    })
    .catch((error) => {
      return Promise.reject(error)
    })

    return response
  }
}

